/**
 * Vraag het project (docs/vraag-het-project.md): één rustig zoekveld onder de fasenbalk met twee tabs —
 * Zoeken (notities, mails, meetings, Teams, dossier van dít project) en Vraag (AI): een antwoord met
 * klikbare [1], [2] naar de bronnen, of "Niet gevonden in de projectcorrespondentie."
 *
 * Praat rechtstreeks met de engine (AI_ENGINE_URL + MSAL-token), zoals TeamsKanaalRegel: identiek op main
 * en develop. Fragmenten komen als tekstdelen [{tekst, mark}] binnen — geen HTML.
 */
import { useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, CircularProgress, InputBase } from '@mui/material';
import { AI_ENGINE_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { T } from '../ProjectOverview/parts/stitch';

const basis = (id) => `${AI_ENGINE_URL}/api/v1/projecten/${encodeURIComponent(id)}`;

/** "De iPad moet mee [1][2]." → [{tekst}, {nummer: 1}, {nummer: 2}, {tekst: '.'}] — getest. */
export function splitsCitaten(antwoord) {
  const delen = [];
  const re = /\[(\d{1,2}(?:\s*,\s*\d{1,2})*)\]/g;
  let pos = 0;
  let m;
  const tekst = String(antwoord || '');
  while ((m = re.exec(tekst)) !== null) {
    if (m.index > pos) delen.push({ tekst: tekst.slice(pos, m.index) });
    m[1].split(',').forEach((n) => delen.push({ nummer: Number(n.trim()) }));
    pos = m.index + m[0].length;
  }
  if (pos < tekst.length) delen.push({ tekst: tekst.slice(pos) });
  return delen;
}

/** Icoon op de bron-chip (de chip-tekst zelf komt van de engine: 'notitie 3 okt', 'mail van Mette'). */
export function bronIcoon(bron) {
  return { notitie: 'sticky_note_2', mail: 'mail', meeting: 'mic', transcript: 'subtitles', teams: 'forum', dossier: 'folder_open' }[bron] || 'description';
}

const tabSx = (actief) => ({
  border: 0, bgcolor: 'transparent', p: 0, cursor: 'pointer', fontFamily: T.fontBody, fontSize: '0.8rem',
  fontWeight: actief ? 700 : 500, color: actief ? T.text : T.outline,
  borderBottom: `2px solid ${actief ? T.tertiary : 'transparent'}`, pb: 0.25,
});

function BronLink({ link, children }) {
  if (!link) return children;
  const sx = { color: 'inherit', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } };
  if (link.startsWith('/')) return <Box component={RouterLink} to={link} sx={sx}>{children}</Box>;
  return <Box component="a" href={link} target="_blank" rel="noreferrer" sx={sx}>{children}</Box>;
}

function Fragment({ delen }) {
  return (
    <Box component="span">
      {(delen || []).map((d, i) => (d.mark
        ? <Box key={i} component="mark" sx={{ bgcolor: T.primaryContainer, color: T.text, px: 0.25, borderRadius: '2px' }}>{d.tekst}</Box>
        : <span key={i}>{d.tekst}</span>))}
    </Box>
  );
}

function Resultaat({ r, nummer, oplichten }) {
  return (
    <Box id={nummer ? `vhp-bron-${nummer}` : undefined} data-testid="vhp-resultaat"
      sx={{ py: 1, borderTop: `1px solid ${T.surfaceHigh}`, bgcolor: oplichten ? T.surfaceLow : 'transparent', transition: 'background-color .4s' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.25 }}>
        {nummer && <Box component="span" sx={{ fontFamily: T.fontBody, fontSize: '0.75rem', fontWeight: 700, color: T.tertiary }}>[{nummer}]</Box>}
        <Box component="span" data-testid="vhp-chip" sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.5, fontFamily: T.fontBody, fontSize: '0.72rem',
          fontWeight: 600, color: T.secondary, bgcolor: T.surfaceLow, borderRadius: '9999px', px: 1, py: 0.1,
        }}>
          <span className="material-symbols-outlined" style={{ fontSize: 14 }}>{bronIcoon(r.bron)}</span>
          {r.label}
        </Box>
        <BronLink link={r.link}>
          <Box component="span" sx={{ fontFamily: T.fontBody, fontSize: '0.8rem', color: T.text, fontWeight: 600 }}>
            {r.titel || 'Openen'}{r.link && !r.link.startsWith('/') ? ' ↗' : ''}
          </Box>
        </BronLink>
      </Box>
      <Box sx={{ fontFamily: T.fontBody, fontSize: '0.8rem', color: T.secondary, lineHeight: 1.5 }}>
        <Fragment delen={r.delen} />
      </Box>
    </Box>
  );
}

export default function VraagHetProject({ projectRecId }) {
  const [modus, setModus] = useState('zoeken');
  const [tekst, setTekst] = useState('');
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState('');
  const [zoek, setZoek] = useState(null);      // {q, resultaten, index}
  const [antwoord, setAntwoord] = useState(null); // {antwoord, gevonden, bronnen, index}
  const [licht, setLicht] = useState(null);
  const lichtTimer = useRef(null);

  const leeg = () => { setZoek(null); setAntwoord(null); setFout(''); };

  const verstuur = async () => {
    const q = tekst.trim();
    if (q.length < 2 || bezig) return;
    setBezig(true); setFout('');
    try {
      const r = modus === 'zoeken'
        ? await authFetch(`${basis(projectRecId)}/zoek?q=${encodeURIComponent(q)}`)
        : await authFetch(`${basis(projectRecId)}/vraag`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ vraag: q }),
        });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || data.error || `HTTP ${r.status}`);
      if (modus === 'zoeken') { setZoek({ ...data, q }); setAntwoord(null); } else { setAntwoord(data); setZoek(null); }
    } catch (e) {
      setFout(e.message);
    } finally { setBezig(false); }
  };

  const springNaar = (n) => {
    const el = document.getElementById(`vhp-bron-${n}`);
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    setLicht(n);
    clearTimeout(lichtTimer.current);
    lichtTimer.current = setTimeout(() => setLicht(null), 1600);
  };

  const index = (zoek || antwoord)?.index;
  const bronnen = antwoord?.bronnen || [];
  const nummers = new Set(bronnen.map((b) => b.nummer));

  return (
    <Box data-testid="vraag-het-project" sx={{ mt: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Box sx={{
          flex: 1, minWidth: 240, display: 'flex', alignItems: 'center', gap: 1, bgcolor: T.surfaceLow,
          borderRadius: '0.5rem', px: 1.25, py: 0.25, '&:focus-within': { bgcolor: T.surface, boxShadow: `0 0 0 1px ${T.outlineVariant}` },
        }}>
          <span className="material-symbols-outlined" style={{ fontSize: 18, color: T.outline }}>{modus === 'zoeken' ? 'search' : 'auto_awesome'}</span>
          <InputBase
            value={tekst} onChange={(e) => setTekst(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') verstuur(); if (e.key === 'Escape') { setTekst(''); leeg(); } }}
            placeholder={modus === 'zoeken' ? 'Zoek in notities, mails, meetings en Teams van dit project' : 'Stel een vraag over dit project'}
            inputProps={{ 'aria-label': 'Vraag het project', maxLength: modus === 'zoeken' ? 500 : 1000 }}
            sx={{ flex: 1, fontFamily: T.fontBody, fontSize: '0.85rem' }}
          />
          {bezig && <CircularProgress size={14} />}
        </Box>
        <Box role="tablist" aria-label="Vraag het project" sx={{ display: 'flex', gap: 1.5 }}>
          {[['zoeken', 'Zoeken'], ['vraag', 'Vraag (AI)']].map(([k, label]) => (
            <Box key={k} component="button" type="button" role="tab" aria-selected={modus === k}
              onClick={() => { setModus(k); leeg(); }} sx={tabSx(modus === k)}>
              {label}
            </Box>
          ))}
        </Box>
      </Box>

      {fout && <Box sx={{ mt: 1, fontFamily: T.fontBody, fontSize: '0.8rem', color: T.error }}>{fout}</Box>}
      {index?.bezig && (zoek || antwoord) && (
        <Box sx={{ mt: 0.75, fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outline }}>
          {index.eerste_keer ? 'Eerste keer: de index wordt opgebouwd, Teams volgt zo.' : 'De index wordt op de achtergrond bijgewerkt.'}
        </Box>
      )}

      {zoek && (
        <Box sx={{ mt: 1 }}>
          {zoek.resultaten?.length
            ? zoek.resultaten.map((r) => <Resultaat key={r.id} r={r} />)
            : <Box sx={{ fontFamily: T.fontBody, fontSize: '0.8rem', color: T.outline }}>Niets gevonden voor “{zoek.q}” in dit project.</Box>}
        </Box>
      )}

      {antwoord && (
        <Box sx={{ mt: 1.25 }}>
          <Box data-testid="vhp-antwoord" sx={{ fontFamily: T.fontBody, fontSize: '0.88rem', color: T.text, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
            {splitsCitaten(antwoord.antwoord).map((d, i) => (d.nummer && nummers.has(d.nummer)
              ? <Box key={i} component="button" type="button" onClick={() => springNaar(d.nummer)}
                aria-label={`Bron ${d.nummer}`}
                sx={{ border: 0, bgcolor: 'transparent', p: 0, mx: 0.15, cursor: 'pointer', color: T.tertiary, fontWeight: 700, fontSize: '0.78rem', verticalAlign: 'super' }}>
                [{d.nummer}]
              </Box>
              : <span key={i}>{d.tekst ?? `[${d.nummer}]`}</span>))}
          </Box>
          {bronnen.length > 0 && (
            <Box sx={{ mt: 1 }}>
              {bronnen.map((b) => <Resultaat key={b.nummer} r={b} nummer={b.nummer} oplichten={licht === b.nummer} />)}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
